import { BaseMode } from '../BaseMode.js';
import { eventBus } from '../../core/utils.js';
import { DiscoveryAPI } from '../../core/discoveryApi.js';
import { API, API_URL, fetchWithTimeout } from '../../core/api.js';
import { getThinkingGroup, pickHeader } from '../../constants/thinkingGroups.js';

/**
 * Discovery Mode - Conversational information gathering with questionnaire interruptions
 * Uses gap-based progress from backend. LLM handles all conversation routing.
 * Questionnaires are served as artifacts via ActionRouter when LLM triggers them.
 *
 * Header Design:
 * - Loading:                "Loading Discovery..."                     (no progress bar, shimmer)
 * - Topic default:          "**Topic Label**"                          (with segmented bar)
 * - Questionnaire active:   "Working on **Questionnaire Name**"        (no bar)
 * - L2 conversation:        "Following up on **Questionnaire Name**"   (no bar)
 * - Thinking states:        Contextual text (e.g., "Reflecting...")    (no bar, shimmer)
 * - Topic complete:         "Completed **Topic Label**"                (all segments filled)
 */
export class DiscoveryMode extends BaseMode {
  /** @type {Object.<string, string>} Placeholder text for different Discovery states */
  static PLACEHOLDERS = {
    DEFAULT: 'Reply...',
  };

  /**
   * Discovery stage types for header display.
   * Each stage determines header text format and progress bar visibility.
   */
  static STAGES = {
    NONE: 'none',                                   // Module-level default (with progress bar)
    QUESTIONNAIRE: 'questionnaire',                 // Questionnaire active in panel (no bar)
    THINKING: 'thinking',                           // LLM processing with contextual text (no bar)
    CONVERSATION: 'conversation',                   // L2 follow-up with Haiku descriptor (no bar)
    COMPLETE: 'complete',                           // Topic complete (100% bar)
    INTERMEDIARY_RESTING: 'intermediary_resting',   // Intermediary card visible, awaiting Yes/Skip (no bar)
    INTRODUCING_QUESTIONNAIRE: 'introducing_questionnaire', // Intro text + launcher card visible, awaiting click
    QUESTIONNAIRE_PAUSED: 'questionnaire_paused'    // User opened then closed the questionnaire panel; launcher in paused/resume state
  };

  constructor(coupleId = null) {
    super({
      name: 'DISCOVERY',
      contextBuilder: null
    });

    // Path state
    this.currentPathId = null;
    this.currentPathMetadata = null;

    // Topic state (topic-level progress tracking for header)
    this.currentTopicId = null;
    this.currentTopicLabel = null;
    this.currentTopicDescription = null;
    this.topicProgress = 0;

    // L3 deep-exploration labels. When set, the top row renders
    // "<topic> - Deep Exploration" with no bar/percent, and the bottom
    // row shows the section name.
    this.currentL3TopicLabel = null;
    this.currentL3SectionName = null;

    // Questionnaire interruption tracking
    this.questionnaireActive = false;
    this.currentQuestionnaireId = null;

    // Gap-based progress (from backend)
    this.percentComplete = 0;
    this.isPathComplete = false;

    // Staleness guard for the two progress readers. Both write the fields
    // above from an awaited response, and so does the completion ceremony —
    // synchronously, mid-flight. Bumped by every read AND by every
    // authoritative local write, so a response that predates the current state
    // is dropped instead of winning on arrival order. See _applyProgress.
    this._progressEpoch = 0;

    // Completion pin. The epoch guard only drops responses whose read was
    // OPENED before the pin — a refetch opened after setCompleteStage
    // (e.g. the orchestrator's delayed post-L2 retry) carries the current
    // epoch and would overwrite the pinned 100% with the backend's
    // not-yet-scopeComplete percent. While pinned, progress reads are no-ops;
    // cleared wherever path scope resets.
    this._progressPinned = false;

    // Segmented progress (section counts from coreProgressService)
    this.totalSections = 0;
    this.completedSections = 0;

    // Remaining-work figures for the header mode block (path mode only).
    // Reset alongside percentComplete everywhere, or a path switch renders the
    // previous path's "N sections, M minutes remaining" under the new name.
    this.sectionsRemaining = 0;
    this.minutesRemaining = 0;
    // Per-conversation estimate for the CONVERSATION stage label. Backend-owned
    // (constants/discovery.js) and delivered with the progress payload; the
    // default only covers the window before the first read lands.
    this.l2EstimatedMinutes = 5;

    // Stage tracking (for header display)
    this.currentStage = DiscoveryMode.STAGES.NONE;
    this.stageQuestionnaireName = null;  // For QUESTIONNAIRE stage
    this.stageThinkingVariant = null;    // For THINKING stage
    this.stageSectionName = null;        // For CONVERSATION stage ("Following up on **X**")
    this.stageQuestionnaireMinutes = null; // Time estimate appended to QUESTIONNAIRE label
    this._savedSectionName = null;       // Saved sectionName during temporary THINKING stage

    // The section whose arc is open, outliving the stage fields above. Those are
    // moment-scoped — every one of them is null between submission and the
    // follow-up conversation — while a section stays "where the user is" from
    // its intro until it resolves. See openSectionArc.
    this.activeSectionName = null;

    this.coupleId = coupleId;
    this.discoveryAPI = new DiscoveryAPI();

    // L2 conversation progress tracking
    this._l2TurnCount = 0;
    this._l2Final = false;

    // Sonnet escalation state
    this._escalationHistory = [];
    this._lastRenderedMessage = null;
  }

  async onEnter(previousMode, transitionData = {}) {
    console.log(`[DiscoveryMode] Entering with data:`, transitionData);

    // Set path context from transition data
    const pathId = transitionData.pathId || null;
    if (pathId) {
      this.currentPathId = pathId;
    }
    if (transitionData.metadata) {
      this.currentPathMetadata = transitionData.metadata;
    }

    // L3 deep-exploration context. switchMode is invoked from _startL3Session
    // with these fields when the user opts into a deep dive. Detected by
    // presence of l3SectionCode/l3SectionName.
    if (transitionData.l3SectionCode || transitionData.l3SectionName) {
      this.currentL3TopicLabel =
        transitionData.subcategoryName ||
        transitionData.l3SectionName ||
        null;
      this.currentL3SectionName = transitionData.l3SectionName || null;
    } else {
      // Re-entering a non-L3 scope clears any stale L3 labels so the next
      // top-row render falls back to path/topic/issue.
      this.currentL3TopicLabel = null;
      this.currentL3SectionName = null;
    }

    // Reset state
    this.questionnaireActive = false;
    this.currentQuestionnaireId = null;
    this.percentComplete = 0;
    this.isPathComplete = false;
    this.totalSections = 0;
    this.completedSections = 0;
    this.sectionsRemaining = 0;
    this.minutesRemaining = 0;
    this.l2EstimatedMinutes = 5;
    this._progressPinned = false;
    this._clearStage();
    this._escalationHistory = [];
    this._lastRenderedMessage = null;

    // Set up event listeners
    this._boundQuestionnaireCompleted = this._handleQuestionnaireCompleted.bind(this);
    this._boundPathSelected = this._handlePathSelected.bind(this);

    eventBus.on('questionnaire:completed', this._boundQuestionnaireCompleted);
    eventBus.on('discovery:path-selected', this._boundPathSelected);

    // Fetch topic progress if entering with a topic
    if (this.currentTopicId) {
      await this._fetchTopicProgress();
    } else if (this.currentPathId) {
      await this._fetchPathProgress();
    }

    this._setPlaceholder('DEFAULT');
  }

  async onExit(nextMode, transitionData = {}) {
    console.log(`[DiscoveryMode] Exiting`);

    // Clean up event listeners
    if (this._boundQuestionnaireCompleted) {
      eventBus.off('questionnaire:completed', this._boundQuestionnaireCompleted);
    }
    if (this._boundPathSelected) {
      eventBus.off('discovery:path-selected', this._boundPathSelected);
    }

    // Reset state when leaving discovery
    if (nextMode.name !== 'DISCOVERY') {
      this.currentPathId = null;
      this.currentPathMetadata = null;
      this.currentQuestionnaireId = null;
      this.questionnaireActive = false;
      this.percentComplete = 0;
      this.isPathComplete = false;
      this.totalSections = 0;
      this.completedSections = 0;
      this.sectionsRemaining = 0;
      this.minutesRemaining = 0;
      this.l2EstimatedMinutes = 5;
      this._progressPinned = false;
      this.currentTopicId = null;
      this.currentTopicLabel = null;
      this.currentTopicDescription = null;
      this.topicProgress = 0;
      this.currentL3TopicLabel = null;
      this.currentL3SectionName = null;
      this._clearStage();
    }
    this._escalationHistory = [];
    this._lastRenderedMessage = null;
  }

  /**
   * Clear escalation history. Called at state advance points
   * (questionnaire resolve, button clicks) to prevent stale context.
   */
  clearEscalationHistory() {
    this._escalationHistory = [];
  }

  getRoutingStrategy(message, _context) {
    // L2 conversation — user text goes to Sonnet normally
    if (this.currentStage === DiscoveryMode.STAGES.CONVERSATION) {
      return 'llm';
    }

    // Action buttons pending and user typed text — classify intent
    if (message?.trim() && window.actionButtons?.hasActiveButtons()) {
      return 'hybrid';
    }

    // Fallback — backend DiscoveryStrategy handles safety net
    return 'llm';
  }

  /**
   * Handle user text when action buttons are pending.
   *
   * Escalates to a Sonnet agent with full stage context and the action-mapped
   * tool palette. Sonnet handles intent matching, answering questions, and mode
   * switches (via the switchMode tool) within the current stage.
   *
   * (A Haiku Tier-1 classifier preceded this, but its `/classify-intent` endpoint
   * was removed: the call 404'd on every request and silently degraded to this
   * Sonnet path, so the dead tier has been deleted.)
   *
   * @param {string} message - User's text input
   * @param {Object} _context - Message context
   * @returns {Promise<Object>} Response object
   */
  async generateHybridResponse(message, _context) {
    const activeButtons = window.actionButtons?.getActiveButtons() || [];
    const chatManager = window.modeOrchestrator?.chatManager;

    if (!activeButtons.length) {
      // No buttons — shouldn't happen, but fall through to LLM
      return { type: 'instant', text: '' };
    }

    try {
      return await this._escalateToSonnet(message, activeButtons);
    } catch (error) {
      console.error('[DiscoveryMode] Hybrid response error:', error);
      await chatManager?.deactivateThinkingIndicator();
      return { type: 'instant', text: '' };
    }
  }

  /**
   * Escalate to Sonnet agent with full stage context and action tools.
   * Replaces the narrow Haiku Tier 2 fallback with an intelligent agent
   * that can answer questions, take actions via tools, and maintain
   * multi-turn context within the current stage.
   * @private
   */
  async _escalateToSonnet(userMessage, activeButtons) {
    const chatManager = window.modeOrchestrator?.chatManager;
    const stageContext = {
      stage: this.currentStage,
      activeButtons: activeButtons.map(b => ({ action: b.action, label: b.label })),
      pathName: this.currentPathMetadata?.name,
      pathDescription: this.currentPathMetadata?.description,
      pathId: this.currentPathMetadata?.id,
      priorMessage: this._lastRenderedMessage,
      sectionCode: window.modeOrchestrator?._currentQuestionnaireSectionCode,
      percentComplete: this.percentComplete,
      escalationHistory: this._escalationHistory
    };

    const sessionId = window.modeOrchestrator?._getSessionId();
    if (!sessionId) {
      await chatManager?.deactivateThinkingIndicator();
      return { type: 'instant', text: '' };
    }

    // Send through regular message path (supports tools).
    // Uses sendMessageForStream() — returns { data: { streamUrl, jobId } }
    // compatible with ResponseHandler.handleResponse().
    let job;
    try {
      job = await API.sendMessageForStream(sessionId, userMessage, {
        discoveryEscalation: true,
        stageContext
      });
    } catch (error) {
      console.error('[DiscoveryMode] Failed to start Sonnet escalation stream:', error);
      await chatManager?.deactivateThinkingIndicator();
      window.notificationManager?.showError('Could not send your message. Please try again.');
      this._rerenderActiveButtons(activeButtons);
      return { type: 'instant', text: '' };
    }

    if (!window.responseHandler) {
      console.error('[DiscoveryMode] ResponseHandler unavailable for Sonnet escalation');
      await chatManager?.deactivateThinkingIndicator();
      window.notificationManager?.showError('Could not send your message. Please try again.');
      this._rerenderActiveButtons(activeButtons);
      return { type: 'instant', text: '' };
    }

    let streamResult;
    try {
      streamResult = await window.responseHandler.handleResponse(job);
    } finally {
      // Deactivate thinking indicator regardless of success/failure.
      // Covers: SSE errors, network timeouts, stream failures.
      await chatManager?.deactivateThinkingIndicator();
    }

    // Capture exchange for multi-turn context
    if (streamResult?.response) {
      this._escalationHistory.push(
        { role: 'user', content: userMessage },
        { role: 'assistant', content: streamResult.response }
      );
    }

    // Re-render buttons if Sonnet answered without calling a tool
    if (!streamResult?.pendingActions?.length) {
      this._rerenderActiveButtons(activeButtons);
    }

    return { type: 'instant', text: '' };
  }

  /**
   * Re-render active discovery buttons after a Sonnet response.
   *
   * Re-renders under the group that still *owns* the input region (stash-aware —
   * the escalation runs after the user used the text-fallback, so `_activeButtonGroupId`
   * is already null and only `_stashedButtons.groupId` knows the real id). This keeps
   * an input-mounted group's re-render in the input (its catalog `renderLocation`)
   * instead of leaking back to the transcript under a synthetic `discovery-rerendered`.
   * @private
   */
  _rerenderActiveButtons(buttons) {
    if (window.actionButtons && window.modeOrchestrator?.chatManager) {
      const owningGroupId =
        window.app?.modules?.input?.getOwningButtonGroupId?.() || 'discovery-rerendered';
      const buttonGroup = window.actionButtons.createButtonGroup(
        buttons.map(b => ({ label: b.label, action: b.action })),
        { groupId: owningGroupId }
      );
      window.modeOrchestrator.chatManager.messageList.addInteractiveComponent(buttonGroup, {
        type: 'action-buttons',
        data: { groupId: owningGroupId }
      });
    }
  }

  async generateProgrammaticResponse(_message, _context) {
    return { type: 'instant', text: '' };
  }

  // ===== STAGE MANAGEMENT =====

  /**
   * Record that a section's arc is open, for the surfaces that must keep
   * pointing at it once the stage fields have moved on.
   *
   * Called from the three stage setters that name a section, and publicly by
   * the orchestrator's intermediary branch — the one moment a section is mid-arc
   * while every stage it passes through is nameless. A nameless call
   * (`setConversationStage()` with no section) leaves the identity standing
   * rather than blanking it: an unnamed conversation is still this section's
   * conversation. The arc closes on resolution (`setCompleteStage`), on the next
   * section naming itself here, or on a mode/path reset (`_clearStage`,
   * `ModeOrchestrator._updateDiscoveryPath`) — deliberately NOT on the public
   * `clearStage()`, which fires mid-arc the moment a questionnaire is submitted.
   *
   * @param {string|null} name - Display name of the section beginning its arc
   */
  openSectionArc(name) {
    if (name) this.activeSectionName = name;
  }

  /**
   * Set header to show active questionnaire name (no progress bar).
   * Called when a questionnaire is rendered in the side panel.
   *
   * @param {string} questionnaireName - Display name of the questionnaire
   */
  setQuestionnaireStage(questionnaireName, estimatedMinutes = null) {
    this.currentStage = DiscoveryMode.STAGES.QUESTIONNAIRE;
    this.stageQuestionnaireName = questionnaireName;
    this.stageQuestionnaireMinutes = Number.isFinite(estimatedMinutes) ? estimatedMinutes : null;
    this.stageThinkingVariant = null;
    this.openSectionArc(questionnaireName);
    console.log('[DiscoveryMode] Stage → questionnaire:', questionnaireName, this.stageQuestionnaireMinutes ? `(~${this.stageQuestionnaireMinutes} min)` : '');
    eventBus.emit('discovery:stage-changed');
  }

  /**
   * Set header to show contextual thinking text (no progress bar).
   * Called when LLM is processing and thinking dots are visible in chat.
   *
   * @param {string} variant - A header-bearing group key in `thinkingGroups`
   */
  setThinkingStage(variant) {
    this.currentStage = DiscoveryMode.STAGES.THINKING;
    this.stageThinkingVariant = variant;
    this.stageQuestionnaireName = null;
    this.stageQuestionnaireMinutes = null;
    console.log('[DiscoveryMode] Stage → thinking:', variant);
    eventBus.emit('discovery:stage-changed');
  }

  /**
   * Set header to L2 conversation mode.
   * Header renders a static "Following up on **sectionName**" for the full
   * duration of the L2 conversation (the previous Haiku-driven dynamic
   * descriptor was removed to keep the header stable).
   *
   * @param {string|null} [sectionName=null] - Questionnaire/section name for the header
   */
  setConversationStage(sectionName = null) {
    this.currentStage = DiscoveryMode.STAGES.CONVERSATION;
    this.stageSectionName = sectionName;
    this.stageQuestionnaireName = null;
    this.stageQuestionnaireMinutes = null;
    this.stageThinkingVariant = null;
    this.openSectionArc(sectionName);
    console.log('[DiscoveryMode] Stage → conversation:', sectionName || '(no section name)');
    eventBus.emit('discovery:stage-changed');
  }

  /**
   * Set header to topic complete state — all segments filled.
   *
   * Forces `completedSections = totalSections` so the segmented bar renders
   * fully complete even if the next `_fetchTopicProgress()` hasn't returned yet.
   *
   * `pathComplete` additionally pins the chat strip to 100. Section tiers are
   * now written synchronously as each conversation ends, so the backend's
   * percent is current here — but Foundation is deliberately capped below 100
   * until `scopeComplete`, and without the pin the strip would stall a few
   * points short through the whole completion ceremony. Pass it ONLY at true
   * path completion: a topic finishing mid-path also lands here, and pinning
   * 100 there would call a half-walked path done.
   *
   * Reaching a completion stage is an authoritative local write, so it retires
   * the progress epoch: the ceremony runs several seconds of awaits before this
   * call, which is ample room for a read issued earlier in the walk to still be
   * in flight, and on arrival that response would otherwise overwrite the pin
   * with its pre-completion percent.
   *
   * @param {Object} [options]
   * @param {boolean} [options.pathComplete=false] - True when this closes the path
   */
  setCompleteStage({ pathComplete = false } = {}) {
    this._progressEpoch++;
    this.currentStage = DiscoveryMode.STAGES.COMPLETE;
    this.isPathComplete = true;
    if (pathComplete) {
      this.percentComplete = 100;
      // Lock the ceremony's 100%: reads opened AFTER this moment carry the
      // current epoch, so the epoch guard alone cannot stop them.
      this._progressPinned = true;
    }
    if (this.totalSections > 0) {
      this.completedSections = this.totalSections;
    }
    this.stageQuestionnaireName = null;
    this.stageQuestionnaireMinutes = null;
    this.stageThinkingVariant = null;
    // The arc this closes — the popover's highlight has nothing left to sit on.
    this.activeSectionName = null;
    this.resetL2Progress();
    console.log('[DiscoveryMode] Stage → complete');
    eventBus.emit('discovery:stage-changed');
  }

  /**
   * Set header to the intermediary resting state — shown after intermediary
   * content has streamed in and the Yes/Skip buttons are visible.
   */
  setIntermediaryRestingStage() {
    this.currentStage = DiscoveryMode.STAGES.INTERMEDIARY_RESTING;
    this.stageQuestionnaireName = null;
    this.stageQuestionnaireMinutes = null;
    this.stageThinkingVariant = null;
    this.stageSectionName = null;
    console.log('[DiscoveryMode] Stage → intermediary_resting');
    eventBus.emit('discovery:stage-changed');
  }

  /**
   * User opened then closed the questionnaire panel without completing. The
   * launcher card in chat has flipped to its paused/resume state and the
   * user is deciding whether to reopen or move on (Skip button or "Move On").
   *
   * Reuses `stageQuestionnaireName` that was set during the prior QUESTIONNAIRE
   * stage — caller doesn't need to pass it again. Bottom row reads
   * "Paused on <name>" until the user picks up where they left off or advances.
   */
  setQuestionnairePausedStage() {
    // Intentionally don't reset stageQuestionnaireName — it's carried over
    // from setQuestionnaireStage so the label can still show which questionnaire
    // is paused. All other stage-scoped fields clear.
    this.currentStage = DiscoveryMode.STAGES.QUESTIONNAIRE_PAUSED;
    this.stageQuestionnaireMinutes = null;
    this.stageThinkingVariant = null;
    this.stageSectionName = null;
    this.resetL2Progress();
    console.log(`[DiscoveryMode] Stage → questionnaire_paused: ${this.stageQuestionnaireName}`);
    eventBus.emit('discovery:stage-changed');
  }

  /**
   * Intro text has finished streaming and the questionnaire launcher card is
   * visible in chat, awaiting the user's click (or Skip). Bottom row reads
   * "Introducing <name>" until questionnaire:opened transitions us to
   * QUESTIONNAIRE stage (or the user skips and the loop advances).
   *
   * @param {string} questionnaireName - Display name of the questionnaire being introduced
   */
  setIntroducingQuestionnaireStage(questionnaireName) {
    this.currentStage = DiscoveryMode.STAGES.INTRODUCING_QUESTIONNAIRE;
    this.stageQuestionnaireName = questionnaireName || null;
    this.stageQuestionnaireMinutes = null;
    this.stageThinkingVariant = null;
    this.stageSectionName = null;
    this.openSectionArc(questionnaireName);
    this.resetL2Progress();
    console.log(`[DiscoveryMode] Stage → introducing_questionnaire: ${questionnaireName}`);
    eventBus.emit('discovery:stage-changed');
  }

  /**
   * Clear stage back to path-level default (with progress bar).
   * Called between states and after transitions.
   */
  clearStage() {
    this.currentStage = DiscoveryMode.STAGES.NONE;
    this.stageQuestionnaireName = null;
    this.stageQuestionnaireMinutes = null;
    this.stageThinkingVariant = null;
    this.stageSectionName = null;
    this.resetL2Progress();
    eventBus.emit('discovery:stage-changed');
  }

  /**
   * Clear stage tracking state without emitting event.
   * Used during mode enter/exit/reset.
   * @private
   */
  _clearStage() {
    this.currentStage = DiscoveryMode.STAGES.NONE;
    this.stageQuestionnaireName = null;
    this.stageQuestionnaireMinutes = null;
    this.stageThinkingVariant = null;
    this.stageSectionName = null;
    // Only the private variant drops the arc: it runs on enter/exit/path switch,
    // where no section is open. Its public twin fires mid-arc.
    this.activeSectionName = null;
    this.resetL2Progress();
  }

  // ===== L2 PROGRESS DOTS =====

  // ===== L2 PROGRESS DOTS (dormant — retained deliberately) =====
  //
  // _renderL2DotsContainer has had no caller since the dots were pulled from
  // the header, so nothing below currently reaches the DOM; the public methods
  // only mutate a counter and emit 'discovery:progress-updated'. This whole
  // cluster — plus the .l2-dots* rules in header.css — is kept on purpose for
  // possible restoration. DO NOT remove it in a dead-code sweep without asking.

  /**
   * Activate the L2 progress dots at the start of an L2 conversation.
   * Turn count starts at 1 (the AI's opening message to the user).
   * Must be called BEFORE setConversationStage so state is primed when
   * the stage-changed event fires the header re-render.
   */
  startL2Progress() {
    this._l2TurnCount = 1;
    this._l2Final = false;
  }

  /**
   * Increment the L2 turn counter and trigger a header re-render.
   */
  incrementL2Turn() {
    if (!this._l2TurnCount) return;
    this._l2TurnCount++;
    eventBus.emit('discovery:progress-updated');
  }

  /**
   * Reset dot state (called on stage exit / mode reset).
   * DOM cleanup unnecessary — header re-render on stage change replaces the label.
   */
  resetL2Progress() {
    this._l2TurnCount = 0;
    this._l2Final = false;
  }

  /**
   * Toggle dots between active and finalized states.
   * Called by the validation-loop hooks.
   * @param {boolean} isFinal - true when awaiting validation; false on reopen.
   */
  setL2DotsFinal(isFinal) {
    this._l2Final = isFinal;
    eventBus.emit('discovery:progress-updated');
  }

  /**
   * Compute dot counts for the current turn count and finalized flag.
   * @private
   */
  _l2DotState() {
    if (this._l2Final) {
      return { completed: this._l2TurnCount, active: 0, ghost: 0 };
    }
    return {
      completed: Math.max(this._l2TurnCount - 1, 0),
      active: 1,
      ghost: 2
    };
  }

  /**
   * Render dot spans without the container wrapper.
   * @private
   */
  _renderL2DotsInner() {
    const { completed, active, ghost } = this._l2DotState();
    const parts = [];
    for (let i = 0; i < completed; i++) parts.push('<span class="l2-dots__dot l2-dots__dot--completed"></span>');
    for (let i = 0; i < active; i++)    parts.push('<span class="l2-dots__dot l2-dots__dot--active"></span>');
    for (let i = 0; i < ghost; i++)     parts.push('<span class="l2-dots__dot l2-dots__dot--ghost"></span>');
    return parts.join('');
  }

  /**
   * Render dot container HTML for inclusion in the header strategy output.
   * Returns empty string when not in CONVERSATION stage or no turn count set.
   * @private
   */
  _renderL2DotsContainer() {
    if (this.currentStage !== DiscoveryMode.STAGES.CONVERSATION || !this._l2TurnCount) {
      return '';
    }
    return `<div class="l2-dots" aria-hidden="true">${this._renderL2DotsInner()}</div>`;
  }

  /**
   * Temporarily switch from CONVERSATION to THINKING stage.
   * Saves the sectionName so it can be restored.
   *
   * @param {string} variant - A header-bearing group key in `thinkingGroups`
   * @returns {boolean} Whether the switch was made
   */
  enterThinkingDuringConversation(variant) {
    if (this.currentStage !== DiscoveryMode.STAGES.CONVERSATION) return false;
    this._savedSectionName = this.stageSectionName;
    this.setThinkingStage(variant);
    return true;
  }

  /**
   * Restore CONVERSATION stage after temporary thinking state.
   * No-op if not currently in THINKING stage (already reverted).
   */
  exitThinkingToConversation() {
    if (this.currentStage !== DiscoveryMode.STAGES.THINKING) return;
    this.setConversationStage(this._savedSectionName);
    this._savedSectionName = null;
  }

  // ===== EVENT HANDLERS =====

  /**
   * Handle questionnaire completion — refresh gap progress
   */
  async _handleQuestionnaireCompleted(data) {
    console.log(`[DiscoveryMode] Questionnaire completed:`, data);

    this.questionnaireActive = false;
    this.currentQuestionnaireId = null;
    this._setPlaceholder('DEFAULT');

    // Refresh progress after questionnaire answers are saved
    if (this.currentTopicId) {
      await this._fetchTopicProgress();
    } else if (this.currentPathId) {
      await this._fetchPathProgress();
    }
  }

  /**
   * Handle path selection — reset state and fetch progress for the new path.
   */
  async _handlePathSelected(data) {
    console.log('[DiscoveryMode] Path selected:', data);

    const pathId = data.pathId;
    const pathMetadata = data.pathMetadata;
    this.currentPathId = pathId;
    this.currentPathMetadata = pathMetadata;
    this.questionnaireActive = false;
    this.currentQuestionnaireId = null;
    this.percentComplete = 0;
    this.isPathComplete = false;
    this.totalSections = 0;
    this.completedSections = 0;
    this.sectionsRemaining = 0;
    this.minutesRemaining = 0;
    this.l2EstimatedMinutes = 5;
    this._progressPinned = false;
    this._clearStage();

    if (this.currentTopicId) {
      await this._fetchTopicProgress();
    } else {
      await this._fetchPathProgress();
    }
  }

  /**
   * Refresh whichever progress figures the current entry mode uses — topic
   * counts in guided mode, path flow figures in path mode. Public: the
   * orchestrator calls this when a tier-mutating action lands (decline, L2
   * completion), the moments the bar must move that no questionnaire event
   * covers. Safe to call blind — the progress epoch and the completion pin
   * drop anything stale.
   *
   * @returns {Promise<void>}
   */
  async refreshProgress() {
    if (this.currentTopicId) {
      await this._fetchTopicProgress();
    } else if (this.currentPathId) {
      await this._fetchPathProgress();
    }
  }

  // ===== PROGRESS STALENESS =====

  /**
   * Open a progress read: retires any read already in flight and records the
   * scope this one speaks for.
   *
   * @private
   * @returns {{epoch: number, pathId: string|null, topicId: string|null}}
   */
  _openProgressRead() {
    return {
      epoch: ++this._progressEpoch,
      pathId: this.currentPathId,
      topicId: this.currentTopicId
    };
  }

  /**
   * Whether a read's response is still the newest word on progress. It is not
   * when a later read, an authoritative local write (the completion pin), or a
   * scope change (path switch, leaving discovery) has landed since it went out.
   * Progress must reflect the latest state, not whichever response arrived last.
   *
   * @private
   * @param {{epoch: number, pathId: string|null, topicId: string|null}} read
   * @returns {boolean}
   */
  _progressReadIsCurrent(read) {
    return read.epoch === this._progressEpoch
      && read.pathId === this.currentPathId
      && read.topicId === this.currentTopicId;
  }

  // ===== TOPIC PROGRESS =====

  /**
   * Fetch topic progress from the core progress endpoint.
   * Picks the current topic from the 24-group response.
   * @private
   */
  async _fetchTopicProgress() {
    if (!this.currentTopicId) return;
    const read = this._openProgressRead();

    try {
      const response = await fetchWithTimeout(`${API_URL}/discovery/progress/core`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) return;
      const result = await response.json();
      if (!this._progressReadIsCurrent(read)) return;
      const groups = result?.data?.groups || [];
      const topic = groups.find(g => g.id === this.currentTopicId);
      if (topic) {
        this.topicProgress = topic.progress;
        this.percentComplete = topic.progress;
        this.totalSections = topic.sectionCount || 0;
        this.completedSections = topic.completedSections || 0;
        console.log('[DiscoveryMode] Topic progress:', {
          topicId: this.currentTopicId,
          progress: this.topicProgress,
          completedSections: this.completedSections,
          totalSections: this.totalSections
        });
        eventBus.emit('discovery:progress-updated');
      }
    } catch (error) {
      console.error('[DiscoveryMode] Error fetching topic progress:', error);
    }
  }

  // ===== PATH PROGRESS =====

  /**
   * Fetch path progress from the orchestrator progress endpoint.
   * @private
   */
  async _fetchPathProgress() {
    if (!this.currentPathId) return;
    // The completion ceremony's 100% is authoritative; see _progressPinned.
    if (this._progressPinned) return;
    const read = this._openProgressRead();

    try {
      const result = await this.discoveryAPI.getPathProgress(this.currentPathId);

      if (result?.data) {
        if (!this._progressReadIsCurrent(read) || this._progressPinned) return;
        const flowProgress = result.data.flowProgress;
        if (flowProgress) {
          this.percentComplete = flowProgress.percentComplete || 0;
          this.isPathComplete = flowProgress.isComplete || false;
          // Drives the mode block's "N sections, M minutes remaining" subtitle.
          this.sectionsRemaining = flowProgress.sectionsRemaining || 0;
          this.minutesRemaining = flowProgress.minutesRemaining || 0;
          // ?? rather than ||: a backend that hasn't shipped the field yet must
          // fall back to the estimate, not to "Discussing X (undefined min)".
          this.l2EstimatedMinutes = flowProgress.l2EstimatedMinutes ?? 5;
        }

        console.log('[DiscoveryMode] Path progress:', {
          pathId: this.currentPathId,
          percentComplete: this.percentComplete,
          isPathComplete: this.isPathComplete,
          sectionsRemaining: this.sectionsRemaining,
          minutesRemaining: this.minutesRemaining
        });

        // Notify the header strategy so the ring/subtitle refresh reactively
        // in path mode (mirrors _fetchTopicProgress).
        eventBus.emit('discovery:progress-updated');
      }
    } catch (error) {
      console.error('[DiscoveryMode] Error fetching path progress:', error);
    }
  }

  // ===== HEADER =====

  /**
   * Public snapshot of the current left-block payload. Used by sibling modes
   * (specifically IntakeMode during the Foundation onboarding prereq) that
   * want to render the discovery header while a different mode is active.
   *
   * Returns null when no path/topic context exists — callers should treat
   * a null result as "no foundation header to inherit." The guard matters:
   * _buildHeaderLeftBlock always returns something (worst case the literal
   * "Discovery"), so without it a standalone intake would inherit that.
   *
   * @returns {Object|null}
   */
  getCurrentLeftBlock() {
    if (!this.currentPathId && !this.currentTopicId && !this.currentL3TopicLabel) {
      return null;
    }
    return this._buildHeaderLeftBlock();
  }

  /**
   * Public snapshot of the path context the progress popover needs.
   *
   * Deliberately separate from getCurrentLeftBlock: that payload is a *render*
   * contract ({ title, rightText?, percent? }, declared by
   * HeaderManager.setLeftBlock and HeaderContentService's strategy interface),
   * and popover state is not header-render state. Keeping them apart also lets
   * this one answer the question the popover actually asks — "is there a path
   * to describe?" — which neither the left block nor IntakeMode.pathId can:
   * getCurrentLeftBlock returns a non-null title-only payload for pathless
   * topic/L3 entries, and IntakeMode.pathId defaults to 'foundation'
   * unconditionally.
   *
   * The guard is narrower than getCurrentLeftBlock's three-way check for that
   * reason — topic and L3 entries have no path progress, so they get null
   * rather than an object carrying pathId: null.
   *
   * @returns {Object|null} Path context, or null when there is no path
   */
  getPathContextSnapshot() {
    if (!this.currentPathId) return null;

    return {
      pathId: this.currentPathId,
      percent: Math.round(Number(this.percentComplete) || 0),
      minutesRemaining: Number(this.minutesRemaining) || 0,
      currentQuestionnaireId: this.currentQuestionnaireId || null,
      // Live stage fields first, so nothing changes in the moments they already
      // cover; activeSectionName backstops the rest of the arc. Every stage
      // field is null from the instant a questionnaire is submitted until the
      // follow-up conversation opens — reflecting, the Go Deeper offer, the
      // resting states between — and the id is nulled on submission too, so
      // without the backstop the highlight goes dark for that whole stretch.
      currentQuestionnaireName:
        this.stageQuestionnaireName || this.stageSectionName || this.activeSectionName || null,
      syntheticCurrentRow: null
    };
  }

  /**
   * Header content strategy — mode block on the left, stage-aware text in the
   * middle. DISCOVERY always emits a leftBlock (see _buildHeaderLeftBlock), so
   * the plain #header-mode-label span stays hidden for this mode's lifetime and
   * getModeLabel() is deliberately not overridden.
   *
   * Middle-row stage patterns:
   * - NONE (default):           "" (empty)
   * - QUESTIONNAIRE:            "Questionnaire: **Name** (N min)"
   * - THINKING:                 Contextual text (e.g., "Reflecting...")
   * - CONVERSATION:             "Discussing **Section Name** (5 min)"
   * - COMPLETE:                 "Completed **Topic**"
   * - INTERMEDIARY_RESTING:     "Some things I noticed"
   */
  getHeaderContentStrategy() {
    return {
      generateContent: async (_context) => ({
        leftBlock: this._buildHeaderLeftBlock(),
        middleBlock: { html: this._buildHeaderMiddleHtml() }
      }),

      getUpdateTriggers: () => [
        'discovery:path-selected',
        'discovery:stage-changed',
        'questionnaire:completed',
        'questionnaire:question-answered',
        'discovery:progress-updated'
      ],

      shouldUpdate: () => true
    };
  }

  /**
   * Build the left-block payload. DISCOVERY always emits one — the branching is
   * on entry-mode shape, and only paths get the full treatment:
   *
   *   - L3 deep-exploration: bare topic label.
   *   - Topic / guided mode: topic label only. Same — the segmented bar the old
   *                          top row carried is deliberately not reproduced.
   *   - Path mode:           path name, plus `rightText` ("N sections, M mins
   *                          left") for the header's right-hand control and
   *                          `percent` for the chat-panel progress strip. Every
   *                          shape renders the same plain title on the left.
   *   - Loading / pre-label: generic "Discovery" placeholder, replaced
   *                          reactively once the first orchestrator response
   *                          populates the real label.
   *
   * Never returns null. Callers wanting "is there any discovery context at all?"
   * should use getCurrentLeftBlock(), which guards on the id fields first.
   *
   * @private
   * @returns {Object}
   */
  _buildHeaderLeftBlock() {
    // L3 deep-exploration and topic mode both render as a plain title.
    if (this.currentL3TopicLabel) {
      return { title: this.currentL3TopicLabel };
    }
    if (this.currentTopicLabel) {
      return { title: this.currentTopicLabel };
    }

    const pathName = this.currentPathMetadata?.name || null;
    if (!pathName) {
      return { title: 'Discovery' };
    }

    return {
      title: pathName,
      rightText: this._buildRemainingText(),
      percent: this.percentComplete
    };
  }

  /**
   * Compose the remaining-work control's label: "N sections, M mins left".
   *
   * Deliberately terser than the path row's "N sections left, about M minutes"
   * (DiscoverySection._pathRemainingPhrase) — this sits in the header's
   * right-hand track, sharing it with the close/options/upgrade controls.
   * Same numbers, shorter words.
   *
   * Each clause is dropped when its count is zero; returns '' when there is
   * nothing left to report.
   *
   * @private
   * @returns {string}
   */
  _buildRemainingText() {
    const sections = Number(this.sectionsRemaining) || 0;
    const minutes = Number(this.minutesRemaining) || 0;

    const parts = [];
    if (sections > 0) parts.push(`${sections} ${sections === 1 ? 'section' : 'sections'}`);
    if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'min' : 'mins'}`);
    if (parts.length === 0) return '';

    return `${parts.join(', ')} left`;
  }

  /**
   * Build the middle-row HTML — stage-specific descriptor.
   *
   * The path/topic identity and its progress now live in the left mode block,
   * so this row carries only what is happening right now. NONE renders empty;
   * the header keeps its height from the left block, not from this row.
   *
   * @private
   * @returns {string} HTML string for the middle row.
   */
  _buildHeaderMiddleHtml() {
    const pathName = this.currentPathMetadata?.name || null;

    switch (this.currentStage) {
      case DiscoveryMode.STAGES.QUESTIONNAIRE: {
        const name = this.stageQuestionnaireName || 'the questionnaire';
        const mins = this.stageQuestionnaireMinutes;
        const suffix = Number.isFinite(mins) && mins > 0 ? ` (${mins} min)` : '';
        return `Questionnaire: <strong>${name}</strong>${suffix}`;
      }

      case DiscoveryMode.STAGES.THINKING: {
        // pickHeader re-randomises the variant on every render — do not hoist
        // or memoise the pick, or the variant freezes for the whole stage.
        const group = getThinkingGroup(this.stageThinkingVariant);
        return pickHeader(group) || 'Processing';
      }

      case DiscoveryMode.STAGES.CONVERSATION: {
        const name = this.stageSectionName || pathName || 'this topic';
        return `Discussing <strong>${name}</strong> (${this.l2EstimatedMinutes} min)`;
      }

      case DiscoveryMode.STAGES.INTERMEDIARY_RESTING: {
        return `Some things I noticed`;
      }

      case DiscoveryMode.STAGES.INTRODUCING_QUESTIONNAIRE: {
        const name = this.stageQuestionnaireName || 'the next section';
        return `Working on <strong>${name}</strong>`;
      }

      case DiscoveryMode.STAGES.QUESTIONNAIRE_PAUSED: {
        const name = this.stageQuestionnaireName || 'this section';
        return `Working on <strong>${name}</strong>`;
      }

      case DiscoveryMode.STAGES.COMPLETE: {
        const completeLabel = this.currentTopicLabel || pathName || 'Topic';
        return `Completed <strong>${completeLabel}</strong>`;
      }

      default: {
        // L3 deep-exploration: the middle row carries the section name while
        // the block above carries the topic.
        if (this.currentL3SectionName) {
          return this.currentL3SectionName;
        }

        // NONE — the mode block carries identity and progress; nothing to say.
        return '';
      }
    }
  }

  filterHistory(allMessages) {
    const lastModeSwitch = allMessages.findLastIndex(
      msg => msg.metadata?.event === 'mode_switched' && msg.metadata?.toMode === 'DISCOVERY'
    );

    if (lastModeSwitch === -1) {
      return allMessages.filter(msg => msg.mode === 'DISCOVERY');
    }

    return allMessages.slice(lastModeSwitch);
  }

  /**
   * Set the chat input placeholder text
   * @param {string} placeholderKey - Key from PLACEHOLDERS static object
   * @private
   */
  _setPlaceholder(placeholderKey) {
    const text = DiscoveryMode.PLACEHOLDERS[placeholderKey];
    if (text && window.app?.modules?.input) {
      window.app.modules.input.setPlaceholder(text);
    }
  }
}
